import type { InvoiceStateContext } from "@talentkontor/domain";
import type { StaffWorkspace } from "../../../app/session";
import type { InvoiceView } from "../../../model/invoice-view";
import { t } from "../../../lib/sprache";
import { actionState } from "./aktionen";

/**
 * K-542: Zeilenmenü der Rechnungsliste mit den Schritten, die die Detailseite für den Stand anbietet (dieselben Bedingungen wie
 * im ActionPanel von RechnungDetail.tsx). Jeder Punkt führt auf die Detailseite mit ?aktion=…, dort öffnet sich derselbe Dialog
 * mit Rückfrage, nie still; ist die Aktion dort inzwischen nicht mehr möglich, sagt die Seite das. Die Detailseite bleibt die
 * letzte Instanz, die Liste rechnet nur vor.
 */
export type ZeilenAktion =
  | "pruefen" | "korrektur" | "ablehnen"
  | "an-marke" | "erneut-senden"
  | "zahlung" | "bestaetigen" | "nachfragen" | "frist" | "mahnsperre";

/** Stände, in denen die Detailseite den Bereich „Zahlung“ bzw. „Auszahlung“ zeigt (inRun in RechnungDetail.tsx) */
const IM_LAUF = ["sent", "due_soon", "due_today", "overdue", "dunning", "payment_reported", "money_missing", "collection", "payout_confirmed", "delivery_pending", "delivery_failed"];

export function zeilenAktionen(v: Pick<InvoiceView, "row" | "record" | "state">, ws: StaffWorkspace, ctx: InvoiceStateContext): ZeilenAktion[] {
  const inv = v.row;
  const state = v.state.state;
  const a = (x: Parameters<typeof actionState>[1]) => actionState(v.record, x, ws, ctx).allowed;
  const incoming = inv.kind === "talent_to_agency";
  const agencyIssued = inv.kind === "agency_to_brand" || inv.kind === "agency_to_talent";
  const unzugestellt = state === "delivery_failed" || state === "delivery_pending";
  const imLauf = IM_LAUF.includes(state) || (state === "payout_overdue" && !!inv.sent_at);
  const raus: ZeilenAktion[] = [];
  // wie Review in RechnungDetail.tsx: Zur Korrektur und Ablehnen mit dem Recht „Rechnungen freigeben“
  if (state === "in_review") {
    raus.push("pruefen");
    if (ws.can("approve_invoices")) raus.push("korrektur", "ablehnen");
  }
  if ((state === "approved" || state === "send_overdue") && inv.kind === "agency_to_brand" && a("send_to_brand")) raus.push("an-marke");
  if (state === "delivery_failed" && a("resend_to_brand")) raus.push("erneut-senden");
  else if (imLauf && inv.kind === "agency_to_brand" && !unzugestellt && a("resend_to_brand") && !(inv.imported && !inv.pdf_path)) raus.push("erneut-senden");
  if (imLauf) {
    if (agencyIssued && ws.can("confirm_payments")) raus.push("zahlung");
    if (!agencyIssued && inv.talent_payment_response === "paid" && a("confirm_payment")) raus.push("bestaetigen");
    if (a("request_payment_check")) raus.push("nachfragen");
    if (!unzugestellt && a("extend_due_date")) raus.push("frist");
  }
  if (!incoming && imLauf && !unzugestellt && a(inv.dunning_blocked ? "lift_dunning_block" : "set_dunning_block")) raus.push("mahnsperre");
  return raus;
}

export function zeilenAktionText(x: ZeilenAktion, inv: { dunning_blocked?: boolean | null; kind: string }): string {
  switch (x) {
    case "pruefen": return t("Prüfen und freigeben", "Review and approve");
    case "korrektur": return t("Zur Korrektur zurückgeben", "Return for correction");
    case "ablehnen": return t("Ablehnen", "Decline");
    case "an-marke": return t("An die Marke senden", "Send to the brand");
    case "erneut-senden": return t("Erneut an die Marke senden", "Send to the brand again");
    case "zahlung": return t("Zahlungseingang erfassen", "Record incoming payment");
    case "bestaetigen": return t("Zahlung bestätigen", "Confirm payment");
    case "nachfragen": return t("Beim Creator nachfragen", "Ask the creator");
    case "frist": return t("Frist verlängern", "Extend deadline");
    case "mahnsperre": return inv.dunning_blocked ? t("Mahnsperre aufheben", "Lift dunning block") : t("Mahnsperre setzen", "Set dunning block");
  }
}
